import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CommonArgs, UnsupportedNodeError } from './utils'

/**
 * TODO ({ key: string; value: any }[] -> object)
 *
 * @param todo TODO
 * @example
 * TODO
 * // ->
 * TODO
 */
export interface If<out T = any> {
  /** {@link If} */
  readonly [Key.If]:
    | readonly If.Case<T>[] // w/o else
    | readonly [...(readonly If.Case<T>[]), Expression<T>] // w/ else
}
export namespace If {
  export const isIf: TypeChecker<If> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<If>({
      [Key.If]: TypeChecker.Factory.custom(
        (val: any): val is If[Key.If] =>
          Array.isArray(val) &&
          val.length >= 1 &&
          val.slice(0, val.length - 1).every(CommonArgs.isTwoExpressions) &&
          CommonArgs.isExpressionOrTwoExpressions(val[val.length - 1]),
        { typeDescription: `Fn.If['${Key.If}']` },
      ),
    }),
  ).usingAlias('Fn.If')
  export const assertIsIf: TypeAsserter<If> = TypeAsserter.Factory.from(isIf)

  export type Case<T = any> = readonly [
    when: Expression<boolean>,
    then: Expression<T>,
  ]

  /**
   * @see {@link Node}
   */
  export class Node<
    T extends Object.Constraint = Object.Constraint,
  > extends fnNode.Node<
    T,
    Key.If,
    {
      readonly cases: readonly {
        // NOTE: cannot be value expression because then we wouldn't need to do
        // a check
        readonly when: fnNode.Node
        readonly then: expressionNode.Node
      }[]
      readonly else: expressionNode.Node
    }
  > {
    // NOTE: done as getter in case underlying nodes have getters
    public get type(): common.value.types.Type | undefined {
      return common.value.types.Intrinsic.Generalized.from(
        ...[...this.arg.cases.map(({ then }) => then), this.arg.else]
          .map(node => node.type)
          .filter(TypeChecker.isNotNullish.raw),
      )
    }

    constructor(arg: Node<T>['arg']) {
      super(Key.If, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<If> = {
    createNode: (fn, context) => {
      const arg: {
        // Mutable two-levels deep
        -readonly [K in keyof Node['arg']]: Mutable<Node['arg'][K]>
      } = {
        cases: [],
        // Defaults to undefined
        else: new expressionNode.ForValue(undefined),
      }
      for (const caseOrExpression of fn[Key.If]) {
        if (TypeChecker.isReadonlyArray(caseOrExpression)) {
          // If this is a case...
          const [whenExpression, thenExpression] = caseOrExpression
          const when = expressionNode.from(whenExpression, context)
          const then = expressionNode.from(thenExpression, context)
          if (expressionNode.ForValue.isForValue(when)) {
            // And the predicate is a value...
            if (when.value) {
              // If the predicate is truthy, then we can stop here and either:
              if (arg.cases.length) {
                // Make this the else node, if there is at least one possible
                // case before this one (i.e. `IF A THEN B, ELIF true THEN C,
                // ELIF D THEN E, ELSE F` === `IF A THEN B, ELSE C`)
                arg.else = then
                break
              } else {
                // Or if there are no possible cases before this one, then we
                // know this is the desired result (i.e. `IF true THEN A` ===
                // `A`)
                return then
              }
            } else {
              // If the predicate is falsy, then it will never be used, so we
              // can just skip it (i.e. `IF false THEN A, ELIF B THEN C` === `IF
              // B THEN C`)
              continue
            }
          } else if (when instanceof fnNode.Node) {
            arg.cases.push({ then, when })
          } else {
            throw new UnsupportedNodeError()
          }
        } else {
          // If this is the else expression, simply create the else node
          arg.else = expressionNode.from(caseOrExpression, context)
        }
      }
      // If there is at least one case, return the if node, otherwise just
      // return the else node (since that is all that is possible)
      return arg.cases.length ? new Node(arg) : arg.else
    },
    expressionsInArgs: fn => fn[Key.If].flat(),
    typeChecker: isIf,
  }
}
